import React, { useEffect, useRef, useState } from 'react';

import { useHistory } from 'react-router-dom';
import { Form as FormikForm, Formik } from 'formik';

import { Box, Container, Typography } from '@material-ui/core';
import LinearProgress from '@material-ui/core/LinearProgress';

import { CircularProgress, Overlay, StaticAlert } from '@mvrb-frontend/shared-ui';

import Page from '../../components/Page';
import { FileUploadSchema } from '../../constants';
import { useUploadFTPForm, useUserApi } from '../../hooks';

import FileUploadHeader from './FileUploadHeader';
import { useGetBoards } from '../../hooks/getBoards';
import FileUploadForm from './FileUploadForm';
import ErrorIcon from '@material-ui/icons/Error';
import CheckIcon from '@material-ui/icons/Check';
import { getUser, isLoggedIn } from '../../utils/auth';

const user = getUser();
const isLogged = isLoggedIn();

const initialValues = {
  boardId: '',
  staffIds: [],
  projectName: '',
  fileNumber: '',
  description: '',
  additionalStaffNotes: '',
  fileSize: 0,
  file: false,
  email: isLogged ? user.email : '',
};

const FileUpload = () => {
  const history = useHistory();
  const formRef = useRef(null);
  const { upload, errors, success, isFetching, uploadProgress } = useUploadFTPForm();
  const { onLoad: loadBoards, boardItems, isFetching: fetchingBoards } = useGetBoards();
  const { staffOptions, isUserApiFetching: fetchingUsers, fetchStaffOptions } = useUserApi();
  const [staffMembers, setStaffMembers] = useState<{ label?: string; value?: string }[]>([]);
  const [attachment, setAttachment] = useState<any>(null);
  const [reset, setReset] = useState(false);

  useEffect(() => {
    loadBoards();
    fetchStaffOptions();
  }, []);

  useEffect(() => {
    if (staffOptions.length) {
      setStaffMembers(staffOptions);
    }
  }, [staffOptions]);

  useEffect(() => {
    if (success) {
      setAttachment(null);
      setReset(true);
    }
  }, [success]);

  const handleSubmit = async values => {
    if (attachment) {
      setReset(false);
      await upload(values, attachment, formRef);
    }
  };

  useEffect(() => {
    setReset(false);
  }, [reset]);

  const goBack = () => {
    history.goBack();
  };

  return (
    <Page isFetching={false} container={false}>
      <Formik
        initialValues={initialValues}
        validationSchema={FileUploadSchema}
        onSubmit={handleSubmit}
        innerRef={formRef}
      >
        {({ submitForm, isValidating }) => (
          <FormikForm>
            <Overlay loading={fetchingBoards || isValidating || fetchingUsers} />
            <FileUploadHeader
              HeaderText="File Submissions"
              submitButtonText="Upload"
              cancelText="Cancel"
              onSubmit={() => submitForm()}
              onCancelConfirm={() => goBack()}
              isFetching={isFetching || isValidating}
              setReset={setReset}
              setAttachment={setAttachment}
            />

            {isFetching && <LinearProgress />}

            <Box my={4} mx={5}>
              {isFetching && uploadProgress > 0 && uploadProgress < 100 && (
                <Box mx={'auto'} width={'fit-content'}>
                  <CircularProgress value={uploadProgress} />
                </Box>
              )}
              <Container maxWidth="md">
                <Typography variant="body1">
                  When you need to submit files that are too large to send via email, please use this service. Please
                  note that all comments, responses, and documents related to an ongoing review must be submitted under
                  their respective Item for Review. Learn more about{' '}
                  <a
                    href="https://mvlwb.com/sites/default/files/mvlwb_document_submission_standards_-_jan_2019.pdf"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Document Submission Standards
                  </a>
                  .
                </Typography>
                {(errors.length || success) && (
                  <StaticAlert
                    p={2}
                    severity={success ? 'success' : 'error'}
                    icon={success ? <CheckIcon /> : <ErrorIcon />}
                  >
                    <div>
                      {success ? (
                        <Typography>
                          Your file has been successfully uploaded. The selected staff contacts will be notified that it
                          has been submitted.
                        </Typography>
                      ) : (
                        <ul>
                          {errors.map((e, i) => (
                            <li key={i}>{e}</li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </StaticAlert>
                )}

                <FileUploadForm
                  boardItems={boardItems}
                  staffMembers={staffMembers}
                  setAttachment={setAttachment}
                  attachment={attachment}
                  reset={reset}
                />
              </Container>
            </Box>
          </FormikForm>
        )}
      </Formik>
    </Page>
  );
};

export default FileUpload;
